<script setup lang="ts">
const { locale, locales } = useI18n();
const route = useRoute();
const localePaths = useNuxtApp().$LocalePaths as Ref<Record<string, string[]>>;

interface Props {
  textClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  textClass: '',
});

const localeNames: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
};

const availableLocales = computed(() => {
  return locales.value.map((l) => ({
    code: l.code,
    name: localeNames[l.code] || l.code,
  }));
});

const currentLocale = computed(() => locale.value);

function getLocalePath(_locale: string) {
  const currentPath = route.path.split('/').filter(Boolean).pop() || '';
  const paths = localePaths?.value?.[_locale] || [];
  const currentIndex = localePaths?.value?.[currentLocale.value]?.findIndex(
    (path: string) => path === currentPath,
  );

  if (currentIndex !== -1 && currentIndex !== undefined) {
    return `/${_locale}/${paths[currentIndex]}`;
  } else {
    return `/${_locale}`;
  }
}
</script>

<template>
  <div class="lang-switch">
    <template v-for="(locale, index) in availableLocales" :key="locale.code">
      <a
        :href="getLocalePath(locale.code)"
        class="locale-link text-2xs-vl text-2xs-vs"
        :class="[
          { '-is-active': locale.code === currentLocale },
          props.textClass,
        ]"
      >
        <span class="_hidden-vl">{{ locale.code.toUpperCase() }}</span>
        <span class="_hidden-vs">{{ locale.name }}</span>
      </a>
      <span v-if="index < availableLocales.length - 1" class="divider"></span>
    </template>
  </div>
</template>

<style scoped lang="scss">
.lang-switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .locale-link {
    position: relative;
    text-decoration: none;
    color: var(--base-color-foreground);

    transition: color 0.5s var(--base-transition-easing, ease);

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -0.25rem;
      width: 100%;
      background-color: currentColor;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease-in-out;
      height: 2px;
    }

    &.-is-active::after {
      transform: scaleX(1);
    }

    @media (hover: hover) {
      &:hover::after {
        transform: scaleX(1);
      }
    }
  }

  .divider {
    background: var(--base-color-foreground);
    opacity: 0.33;
    height: 0.75rem;
    width: 1px;
  }

  @media (--vs) {
    gap: 0.5rem;
  }
}
</style>
